import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { StatPill } from '../../components/ui/StatPill';
import { formatMoneyCompact } from '../../lib/money';
import { setupProgress, type ScenarioRecord } from '../../lib/scenarioStore';
import styles from './ScenarioCard.module.css';

interface ScenarioCardProps {
  record: ScenarioRecord;
  areaLabel: string;
  onDuplicate: (record: ScenarioRecord) => void;
  onDelete: (id: string) => void;
}

const DATE_FORMAT = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

export function ScenarioCard({ record, areaLabel, onDuplicate, onDelete }: ScenarioCardProps): React.JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const progress = setupProgress(record);
  const isUntitled = record.name.trim().length === 0 || record.name === 'Untitled scenario';

  return (
    <Card interactive className={styles.card}>
      <Link to={`/scenarios/${record.id}`} className={styles.linkOverlay} aria-label={record.name} />

      <div className={styles.topRow}>
        <span className={styles.icon} aria-hidden="true">
          <AreaIcon area={record.input.area} />
        </span>
        <div className={styles.badges}>
          <Badge tone={record.status === 'finalized' ? 'ok' : 'warn'} dot>
            {record.status === 'finalized' ? 'Finalized' : 'Draft'}
          </Badge>
          <div className={styles.menuWrap}>
            <button
              type="button"
              className={styles.menuButton}
              aria-label="Scenario actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen((v) => !v);
              }}
            >
              <KebabIcon />
            </button>
            {menuOpen && (
              <div className={styles.menu} role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.preventDefault();
                    setMenuOpen(false);
                    onDuplicate(record);
                  }}
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuDanger}
                  onClick={(e) => {
                    e.preventDefault();
                    setMenuOpen(false);
                    if (window.confirm(`Delete "${record.name}"? This cannot be undone.`)) {
                      onDelete(record.id);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <p className={isUntitled ? styles.titleMuted : styles.title}>{record.name}</p>

      {record.customerName ? (
        <p className={styles.meta}>
          <BuildingIcon /> {record.customerName}
        </p>
      ) : (
        <p className={styles.metaMuted}>No customer set — click to configure</p>
      )}

      <p className={styles.creatorRow}>
        <Avatar name={record.ownerName ?? 'You'} />
        <span>{record.ownerName ?? 'You'}</span>
        <span className={styles.dot}>·</span>
        <span>{DATE_FORMAT.format(new Date(record.updatedAt))}</span>
      </p>

      {record.summary ? (
        <div className={styles.stats}>
          <StatPill icon={<ClockIcon />}>{formatMoneyCompact(record.summary.incrementalAcrUsd)} ACR</StatPill>
          <StatPill tone="ok">{formatMoneyCompact(record.summary.claimableTotalUsd)}</StatPill>
          <StatPill>{record.summary.eligibleCount} eligible</StatPill>
        </div>
      ) : (
        <div className={styles.progressBlock}>
          <div className={styles.progressLabelRow}>
            <span className={styles.progressLabel}>Setup progress</span>
            <span className={styles.progressFraction}>
              {progress.done}/{progress.total}
            </span>
          </div>
          <ProgressBar value={progress.done} max={progress.total} />
        </div>
      )}

      <div className={styles.bottomRow}>
        <Badge tone="primary">{areaLabel}</Badge>
        <span className={styles.date}>{DATE_FORMAT.format(new Date(record.updatedAt))}</span>
      </div>
    </Card>
  );
}

function AreaIcon({ area }: { area: string }): React.JSX.Element {
  if (area === 'security') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5L13.5 3.5V7.5C13.5 11 11 13.5 8 14.5C5 13.5 2.5 11 2.5 7.5V3.5L8 1.5Z" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    );
  }
  if (area === 'abs') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="4.5" width="13" height="9" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
        <path d="M5.5 4.5V3A1.5 1.5 0 0 1 7 1.5H9A1.5 1.5 0 0 1 10.5 3V4.5" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    );
  }
  if (area === 'marketplace') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 5.5L3 1.5H13L14 5.5M2 5.5V13.5A1 1 0 0 0 3 14.5H13A1 1 0 0 0 14 13.5V5.5M2 5.5H14" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4.5 11.5A3 3 0 0 1 4.5 5.5A3.8 3.8 0 0 1 11.9 4.6A2.8 2.8 0 0 1 11.5 11.5H4.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
      />
    </svg>
  );
}

function KebabIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <circle cx="8" cy="3" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="13" r="1.4" />
    </svg>
  );
}

function BuildingIcon(): React.JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ marginRight: 4 }}>
      <rect x="3" y="2" width="10" height="12" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 5H6.01M10 5H10.01M6 8H6.01M10 8H10.01M6 11H6.01M10 11H10.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ClockIcon(): React.JSX.Element {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 4.5V8L10.3 9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
